import { languages, type Languages } from '../../i18n/i18n.language-config';

export interface AgencyShellInput {
  /** `broker_experience_v2`. */
  readonly brokerV2: boolean;
  /** The agency's opt-in to the next dashboard (`OPT_IN_NEW_AGENT_EXPERIENCE`). */
  readonly optInNewAgent: boolean;
  /** The platform language: `en-US`, `en-GB` or `en-AU`. */
  readonly region: Languages;
}

export interface AgencyShell {
  /** The navigation down the page's side: the broker rail, the legacy sidebar, or none. */
  readonly sidebar: 'rail' | 'legacy' | 'none';
  /** The header across the top: the dashboard's own, or the console's main header. */
  readonly header: 'dashboard' | 'main';
  /** Which dashboard `/agency/dashboard` shows. */
  readonly dashboard: 'v2' | 'next' | 'legacy';
  /** Whether the header carries the mega menu. */
  readonly megaMenu: boolean;
}

const NEXT_DASHBOARD_REGIONS: readonly Languages[] = [
  languages['en-GB'],
  languages['en-AU'],
];

/**
 * How the agency persona's page is dressed, decided in one place. The broker experience wins over everything. With
 * it off, nothing changes from today: the opt-in picks the dashboard header and mega menu, the opt-in or a UK or AU
 * platform picks the next dashboard, and only a US agency that has not opted in keeps the legacy sidebar.
 */
export function agencyShell({
  brokerV2,
  optInNewAgent,
  region,
}: AgencyShellInput): AgencyShell {
  if (brokerV2) {
    return {
      sidebar: 'rail',
      header: 'dashboard',
      dashboard: 'v2',
      megaMenu: false,
    };
  }

  const header = optInNewAgent ? 'dashboard' : 'main';
  const megaMenu = optInNewAgent;
  if (optInNewAgent || NEXT_DASHBOARD_REGIONS.includes(region)) {
    return { sidebar: 'none', header, dashboard: 'next', megaMenu };
  }
  const sidebar = region === languages['en-US'] ? 'legacy' : 'none';
  return { sidebar, header, dashboard: 'legacy', megaMenu };
}
